import { useState, type FormEvent } from 'react'
import { LockKeyhole, LogIn, Mail, UserPlus } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
import { api } from '../api/client'
import type { RegistrationConfig } from '../api/types'
import { useI18n } from '../i18n'
import { LanguageSelect } from './LanguageSelect'
import { useEffect } from 'react'

export function LoginForm({ mode }: { mode: 'admin' | 'user' }) {
  const { t } = useI18n()
  const navigate = useNavigate()
  const [error, setError] = useState('')
  const [pending, setPending] = useState(false)
  const [regConfig, setRegConfig] = useState<RegistrationConfig | null>(null)

  useEffect(() => {
    if (api.canAccess(mode)) {
      navigate(mode === 'admin' ? '/admin/dashboard' : '/user/mail', { replace: true })
    }
  }, [mode, navigate])

  useEffect(() => {
    if (mode === 'user') {
      api.get<RegistrationConfig>('/auth/registration-config').then(setRegConfig).catch(() => {})
    }
  }, [mode])

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault(); setPending(true); setError('')
    const form = new FormData(event.currentTarget)
    try {
      const result = await api.login(String(form.get('identity')), String(form.get('password')))
      api.setSession(result.accessToken, result.user.role, result.user.email)
      if (mode === 'admin' && result.user.role !== 'admin') { api.setToken(); throw new Error('Administrator access is required.') }
      navigate(mode === 'admin' ? '/admin/dashboard' : '/user/mail')
    } catch (reason) { setError(reason instanceof Error ? reason.message : 'Unable to sign in.') }
    finally { setPending(false) }
  }

  function handleSSOLogin() {
    window.location.href = `${import.meta.env.VITE_API_URL ?? '/api/v1'}/auth/sso/authorize`
  }

  const prefix = mode === 'admin' ? 'auth.admin' : 'auth.user'
  return <main className="grid min-h-screen place-items-center bg-gradient-to-br from-primary/5 via-background to-primary/10 p-4 sm:p-6">
    <section className="panel w-full max-w-[26rem] p-6 sm:p-8 shadow-lg">
      <div className="mb-7 flex items-center justify-between"><div className="flex items-center gap-3"><div className="rounded-xl bg-primary p-2.5 text-primary-foreground shadow-sm"><Mail size={20}/></div><span className="font-semibold tracking-tight">{t('common.appName')}</span></div><LanguageSelect /></div>
      <h1 className="text-2xl font-bold tracking-tight">{t(`${prefix}Title`)}</h1><p className="mt-1.5 text-sm text-muted-foreground">{t(`${prefix}Subtitle`)}</p>
      <form className="mt-6 space-y-4" onSubmit={submit}>
        <label className="block text-sm font-medium">{t('common.email')}<input className="field mt-1.5" name="identity" type="text" autoComplete="username" required /></label>
        <label className="block text-sm font-medium">{t('common.password')}<input className="field mt-1.5" name="password" type="password" autoComplete="current-password" required /></label>
        <div className="flex justify-between text-sm"><label className="flex items-center gap-2 text-muted-foreground"><input type="checkbox" className="rounded" /> {t('auth.remember')}</label><button type="button" className="text-primary hover:underline">{t('auth.forgotPassword')}</button></div>
        {error && <p role="alert" className="rounded-lg bg-destructive/10 px-3 py-2.5 text-sm text-destructive">{error}</p>}
        <button disabled={pending} className="btn-primary flex w-full items-center justify-center gap-2"><LockKeyhole size={16}/>{pending ? t('common.loading') : t('common.signIn')}</button>
      </form>

      {mode === 'user' && regConfig?.oidcEnabled && (
        <div className="mt-5">
          <div className="relative my-4"><div className="absolute inset-0 flex items-center"><div className="w-full border-t" /></div><div className="relative flex justify-center text-xs"><span className="bg-card px-3 text-muted-foreground">{t('common.or')}</span></div></div>
          <button onClick={handleSSOLogin} className="btn-secondary flex w-full items-center justify-center gap-2"><LogIn size={16} />{t('auth.ssoLogin')}</button>
        </div>
      )}

      {mode === 'user' && regConfig?.publicRegistration && (
        <p className="mt-5 text-center text-sm text-muted-foreground">{t('auth.noAccount')} <Link to="/user/register" className="font-medium text-primary hover:underline">{t('common.register')}</Link></p>
      )}

      <p className="mt-6 text-center text-xs text-muted-foreground">{t('auth.secureNote')}</p>
    </section>
  </main>
}
